import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, CalendarDays, Wallet, Users, Tag, Check } from "lucide-react";
import { MEMBERSHIPS, planDurationLabel } from "@/data/plans";

export default function SkillModal({ course, index = 0, onClose }) {
  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const radioRefs = useRef([]);
  const [sel, setSel] = useState(1); // default: Silver (3 months)
  const navigate = useNavigate();

  useEffect(() => {
    if (course) setSel(1);
  }, [course]);

  useEffect(() => {
    if (!course) return;
    triggerRef.current = document.activeElement;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => modalRef.current?.querySelector("[data-testid='skill-modal-close']")?.focus(), 60);

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll("button, a[href], [tabindex]:not([tabindex='-1'])");
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      document.removeEventListener("keydown", onKey);
      triggerRef.current?.focus?.();
    };
  }, [course, onClose]);

  const plan = MEMBERSHIPS.plans[sel];

  const onRadioKey = (e, i) => {
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % MEMBERSHIPS.plans.length;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i + MEMBERSHIPS.plans.length - 1) % MEMBERSHIPS.plans.length;
    if (next !== null) {
      e.preventDefault();
      setSel(next);
      radioRefs.current[next]?.focus();
    }
  };

  const claimFree = () => {
    onClose();
    navigate(`/play-first-pay-later?course=${encodeURIComponent(course.name)}&membership=${encodeURIComponent(plan.name)}`);
  };

  const tiles = course
    ? [
        { icon: Clock, label: "Duration", value: planDurationLabel(plan.months) },
        { icon: CalendarDays, label: "Sessions", value: plan.perks[0] },
        {
          icon: Wallet,
          label: "Fees",
          value: (
            <>
              {plan.price}
              {plan.valuePrice && <span className="ml-1.5 text-xs text-ink/60 line-through font-medium">{plan.valuePrice}</span>}
            </>
          ),
        },
        { icon: Users, label: "Class Format", value: course.classFormat },
      ]
    : [];

  const offersLine = course
    ? (() => {
        const isBundle = plan.perks.some((p) => /everything in/i.test(p));
        const detail = plan.perks.length > 2 ? plan.perks.slice(2) : isBundle ? MEMBERSHIPS.plans[2].perks.slice(2) : [];
        const base = plan.perks.length <= 2 ? plan.perks.join(" · ") : null;
        return [base, plan.savingsBadge, ...detail].filter(Boolean).join(" · ");
      })()
    : "";

  return (
    <AnimatePresence>
      {course && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-6"
          style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          data-testid="skill-modal-overlay"
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${course.name} course details`}
            className="bg-white w-full sm:max-w-[720px] rounded-t-[20px] sm:rounded-[20px] shadow-2xl max-h-[88vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="skill-modal"
          >
            <div className="relative h-[200px]">
              <img src={course.image} alt={course.alt} className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 text-[11px] font-extrabold tracking-[0.2em] bg-brand text-white px-3 py-1.5 rounded-full" data-testid="skill-modal-number">
                {String(index + 1).padStart(2, "0")}
              </span>
              <button
                onClick={onClose}
                data-testid="skill-modal-close"
                aria-label="Close details"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 text-ink flex items-center justify-center hover:bg-brand hover:text-white transition-colors"
              >
                <X size={17} />
              </button>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-[11px] tracking-[0.25em] text-brand font-bold uppercase" data-testid="skill-modal-tag">{course.tag}</p>
              <h3 className="mt-1.5 font-body font-extrabold text-2xl sm:text-3xl text-ink tracking-tight" data-testid="skill-modal-name">{course.name}</h3>
              <p className="text-sm text-muted mt-1.5" data-testid="skill-modal-tagline">{course.tagline}</p>

              <p className="mt-6 text-[11px] tracking-[0.25em] text-brand font-bold uppercase">Choose your membership</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Choose your membership" data-testid="skill-modal-plans">
                {MEMBERSHIPS.plans.map((p, i) => {
                  const active = i === sel;
                  return (
                    <button
                      key={p.name}
                      ref={(el) => (radioRefs.current[i] = el)}
                      role="radio"
                      aria-checked={active}
                      tabIndex={active ? 0 : -1}
                      onClick={() => setSel(i)}
                      onKeyDown={(e) => onRadioKey(e, i)}
                      data-testid={`skill-modal-plan-${i}`}
                      className={`rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand/40 ${p.tint} ${
                        active ? "border-brand shadow-[0_0_0_2px_rgba(224,73,47,0.25)]" : "hover:brightness-95"
                      }`}
                    >
                      {p.tag && <span className={`inline-block text-[8px] font-extrabold tracking-[0.12em] rounded-full px-2 py-0.5 mb-1 ${p.badgeClass || "bg-brand text-white"}`} data-testid={`skill-modal-tag-${i}`}>{p.tag}</span>}
                      <span className={`block text-xs font-bold ${active ? "text-brand" : "text-ink"}`}>{p.short}</span>
                      <span className="block mt-1">
                        <span className={`text-base font-extrabold ${active ? "text-brand" : "text-ink"}`}>{p.price}</span>
                        {p.priceSuffix && <span className="ml-1 text-[11px] font-bold text-teal" data-testid={`skill-modal-save-${i}`}>({p.priceSuffix})</span>}
                        {p.valuePrice && (
                          <span className="block text-[11px] text-neutral-600 font-bold mt-0.5">
                            Actual Value: <span className="line-through">{p.valuePrice}</span>
                          </span>
                        )}
                      </span>
                      {p.savingsBadge && <span className="inline-block text-[10px] font-bold text-neutral-600 bg-neutral-200/80 rounded px-1.5 py-0.5 mt-1">{p.savingsBadge}</span>}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3" data-testid="skill-modal-info-grid">
                {tiles.map((t) => (
                  <div key={t.label} className="rounded-2xl border border-ink/10 bg-paper p-4">
                    <t.icon size={16} className="text-brand" />
                    <p className="mt-2 text-[10px] tracking-[0.18em] text-muted font-bold uppercase">{t.label}</p>
                    <p className="text-sm font-semibold text-ink mt-0.5" data-testid={`skill-modal-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>{t.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl bg-brand/10 border border-brand/20 px-4 py-3.5 flex items-start gap-2.5" data-testid="skill-modal-offers">
                <Tag size={15} className="text-brand mt-0.5 shrink-0" />
                <p className="text-sm text-ink/85">
                  <span className="font-bold text-ink">Includes:</span> {offersLine}
                </p>
              </div>

              <div className="mt-6" data-testid="skill-modal-learn">
                <p className="text-[11px] tracking-[0.25em] text-brand font-bold uppercase">What you'll learn</p>
                <ul className="mt-3 space-y-2.5">
                  {course.learnPoints.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-ink/80">
                      <span className="w-5 h-5 rounded-full bg-teal/10 text-teal flex items-center justify-center shrink-0 mt-0.5"><Check size={12} /></span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={claimFree}
                  data-testid="skill-modal-claim"
                  className="flex-1 rounded-full bg-brand text-white font-semibold text-sm px-7 py-3.5 hover:bg-ink transition-colors duration-300"
                >
                  Book Your 2 Trial Sessions
                </button>
                <button
                  onClick={onClose}
                  data-testid="skill-modal-close-secondary"
                  className="rounded-full border border-ink/20 text-ink font-semibold text-sm px-7 py-3.5 hover:border-ink transition-colors duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
